import type { z } from 'zod';

import { toStripView } from '../../web/app-home/src/lib/stripView';
import { MiniAppFetchError, throwFetchError } from './fetch-error';

/** Mirrors core Terra's `ApiProblemKind` (`web/app-home/src/lib/apiFetchJson.ts`) minus `network`. */
export type FetchJsonErrorKind = 'http' | 'aborted' | 'malformed';

/**
 * Error from `fetchJson`. Extends `MiniAppFetchError`, so `isFetchError` /
 * `classifyError` / `displayMessageForFetchError` keep working. The server's
 * human text is `userMessage`, from `throwFetchError`'s precedence chain.
 */
export class FetchJsonError extends MiniAppFetchError {
  readonly kind: FetchJsonErrorKind;
  /** The error this one was translated from, when there was one. Declared as an own field because the ES2020 lib has no `Error` cause. */
  readonly cause?: unknown;

  constructor(kind: FetchJsonErrorKind, fields: ConstructorParameters<typeof MiniAppFetchError>[0], cause?: unknown) {
    super(fields);
    this.name = 'FetchJsonError';
    this.kind = kind;
    this.cause = cause;
  }
}

/** True for a 2xx whose body failed its schema (or was not JSON): the write may have committed. */
export function isMalformedResponse(err: unknown): err is FetchJsonError {
  return err instanceof FetchJsonError && err.kind === 'malformed';
}

/** True when the request was cancelled (before or during the body read): the write may have committed. */
export function isAbortedRequest(err: unknown): err is FetchJsonError {
  return err instanceof FetchJsonError && err.kind === 'aborted';
}

/**
 * True when a failed write may still have committed, so the caller should
 * refetch what it just wrote (bd-ea20w saved-refresh rule): a 2xx whose body
 * failed its schema, or a request cut off before the body was read. Mirrors
 * `web/app-home/src/lib/writeMayHaveLanded.ts`. An HTTP failure or network
 * error means the write did not land.
 */
export function writeMayHaveLanded(err: unknown): boolean {
  return isMalformedResponse(err) || isAbortedRequest(err);
}

function describeInput(input: unknown): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  if (input instanceof Request) return input.url;
  return 'unknown';
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

function toHttpError(err: MiniAppFetchError): FetchJsonError {
  return new FetchJsonError('http', {
    code: err.code,
    status: err.status,
    oauthUrl: err.oauthUrl,
    userMessage: err.userMessage,
    message: err.userMessage,
    retryAfterSec: err.retryAfterSec,
  }, err);
}

/**
 * Mini-app counterpart of core Terra's `fetchJson`: one `apiFetch` call, the
 * body parsed through `schema`. Accepts either the throwing `apiFetch` or the
 * raw `apiFetchRaw` from `useApi()`.
 *
 * - non-2xx: `FetchJsonError` kind `http` (code/status/oauthUrl/userMessage);
 * - abort (request or body read): kind `aborted`;
 * - 2xx body that fails the schema: kind `malformed`, never the unparsed
 *   payload. An empty or non-JSON body reaches the schema as `undefined` (as in
 *   core Terra's `readJsonBody`), so a schema that accepts `undefined` takes a
 *   204;
 * - schema drift is logged as paths and issue codes only, never values;
 * - a network `TypeError` propagates unchanged, like the rest of this boundary.
 *
 * The schema is read through a strip view so a tab on an older bundle accepts
 * keys the server added since. The strip view only relaxes `.strict()` objects:
 * a `looseObject` / `.passthrough()` schema still keeps unknown keys.
 *
 * The drift log uses `console.warn` rather than the web logger, which uses
 * `import.meta` and breaks `tsconfig.tests.json` (see `fetch-error.ts`).
 */
export async function fetchJson<S extends z.ZodType, In = RequestInfo | URL, Init = RequestInit & { timeoutMs?: number }>(
  apiFetch: (input: In, init?: Init) => Promise<Response>,
  schema: S,
  input: In,
  init?: Init,
): Promise<z.output<S>> {
  let res: Response;
  try {
    res = await apiFetch(input, init);
    if (!res.ok) await throwFetchError(res);
  } catch (err) {
    if (err instanceof MiniAppFetchError) throw toHttpError(err);
    if (isAbortError(err)) throw new FetchJsonError('aborted', { code: 'ABORTED', status: 0 }, err);
    throw err;
  }

  let raw: unknown;
  try {
    raw = await res.json();
  } catch (err) {
    if (isAbortError(err)) throw new FetchJsonError('aborted', { code: 'ABORTED', status: res.status }, err);
    raw = undefined;
  }
  const parsed = toStripView(schema).safeParse(raw);
  if (!parsed.success) {
    console.warn(
      '[api-validation]',
      describeInput(input),
      parsed.error.issues.map((i) => ({ path: i.path, code: i.code })),
    );
    throw new FetchJsonError('malformed', { code: 'INVALID_RESPONSE', status: res.status });
  }
  return parsed.data;
}
